/**
 * i18n — internationalisation for the Vela web dashboard.
 *
 * Phase 3: 12 languages with lazy loading. Only English is bundled inline;
 * every other locale is a dynamic import so the initial JS bundle doesn't
 * bloat. RTL is applied at the document level when Arabic is active.
 *
 * Fonts are loaded dynamically per-language so a user who never switches
 * to Arabic never downloads Cairo.
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

// English is bundled inline (always needed as fallback).
// Uses the unified translation.json so all `t('nav.dashboard')` etc. keys resolve.
import translationEn from './locales/en/translation.json'

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English',       flag: '🇬🇧', rtl: false, font: null },
  { code: 'ar', label: 'العربية',        flag: '🇸🇦', rtl: true,  font: 'Cairo' },
  { code: 'zh', label: '中文',            flag: '🇨🇳', rtl: false, font: 'Noto Sans SC' },
  { code: 'ja', label: '日本語',           flag: '🇯🇵', rtl: false, font: 'Noto Sans JP' },
  { code: 'th', label: 'ภาษาไทย',        flag: '🇹🇭', rtl: false, font: 'Noto Sans Thai' },
  { code: 'vi', label: 'Tiếng Việt',     flag: '🇻🇳', rtl: false, font: null },
  { code: 'ms', label: 'Bahasa Melayu',  flag: '🇲🇾', rtl: false, font: null },
  { code: 'ru', label: 'Русский',        flag: '🇷🇺', rtl: false, font: null },
  { code: 'es', label: 'Español',        flag: '🇪🇸', rtl: false, font: null },
  { code: 'pt', label: 'Português',      flag: '🇧🇷', rtl: false, font: null },
  { code: 'tr', label: 'Türkçe',         flag: '🇹🇷', rtl: false, font: null },
  { code: 'el', label: 'Ελληνικά',       flag: '🇬🇷', rtl: false, font: null },
] as const

export type LangCode = typeof SUPPORTED_LANGUAGES[number]['code']

const RTL_LANGUAGES = new Set<string>(['ar'])

/** Detect initial language: localStorage → browser → 'en'. */
function detectLanguage(): string {
  try {
    const saved = localStorage.getItem('vela.lang')
    if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) return saved
  } catch { /* private mode */ }
  const browser = navigator.language?.split('-')[0]
  if (browser && SUPPORTED_LANGUAGES.some((l) => l.code === browser)) return browser
  return 'en'
}

void i18n.use(initReactI18next).init({
  lng: detectLanguage(),
  fallbackLng: 'en',
  ns: ['common', 'cases', 'contacts', 'dashboard', 'campaigns', 'widget'],
  defaultNS: 'common',
  resources: {
    en: {
      common: translationEn,
    },
  },
  interpolation: { escapeValue: false },
})

/**
 * Change the active language. Handles:
 *   - Loading the translation file (lazy import)
 *   - Applying RTL to the document
 *   - Loading the language-specific font
 *   - Persisting to localStorage
 */
export async function changeLanguage(code: string): Promise<void> {
  // Load translations lazily for non-English languages.
  if (code !== 'en' && !i18n.hasResourceBundle(code, 'common')) {
    try {
      const mod = await import(`./locales/${code}/translation.json`)
      const data = mod.default ?? mod
      i18n.addResourceBundle(code, 'common', data, true, true)
    } catch {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Translation file for '${code}' not found — falling back to English`)
    }
  }

  await i18n.changeLanguage(code)

  // Persist.
  try { localStorage.setItem('vela.lang', code) } catch { /* private mode */ }

  // RTL.
  applyDirection(code)

  // Font.
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === code)
  if (lang?.font) loadFont(lang.font)
  else document.documentElement.style.fontFamily = "'Inter', system-ui, sans-serif"

  // Lang attribute.
  document.documentElement.lang = code
}

/** Apply or remove RTL direction on the document root. */
export function applyDirection(code: string): void {
  const isRtl = RTL_LANGUAGES.has(code)
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr'
  if (isRtl) {
    document.documentElement.classList.add('rtl')
  } else {
    document.documentElement.classList.remove('rtl')
  }
}

const loadedFonts = new Set<string>()

function loadFont(family: string): void {
  if (loadedFonts.has(family)) {
    document.documentElement.style.fontFamily = `'${family}', sans-serif`
    return
  }
  const encoded = family.replace(/ /g, '+')
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;500;600;700;800&display=swap`
  document.head.appendChild(link)
  loadedFonts.add(family)
  document.documentElement.style.fontFamily = `'${family}', sans-serif`
}

// Apply direction on first load (before React renders).
applyDirection(detectLanguage())

export default i18n
